import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import { getFacultySubmissionReview, postFacultySubmissionOverride } from '../../services/labResults.api';
import {
  ExpandBlock,
  JsonPreview,
  ScoreBadge,
  ScoreBreakdownTable,
  GitEvaluationTable,
  ApiEvaluationTable,
  UiEvaluationTable,
  EvaluatorFlagsBlock,
  FlagsRow,
  RemarksList,
  formatScore,
} from '../../components/labEvaluation/LabEvaluationSections';

function facultyResultsListPath(detail, searchParams) {
  const fromQuery = (searchParams.get('labId') || '').trim();
  const fromDetail = detail?.labId != null ? String(detail.labId).trim() : '';
  const lab = fromDetail || fromQuery;
  return lab ? `/faculty/lab-results?labId=${encodeURIComponent(lab)}` : '/faculty/lab-results';
}

export default function FacultyLabSubmissionDetailPage() {
  const { submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overrideForm, setOverrideForm] = useState({
    totalScore: '',
    grade: '',
    remarks: '',
  });
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideMsg, setOverrideMsg] = useState('');

  const load = async () => {
    if (!submissionId) return;
    setLoading(true);
    setError('');
    try {
      const d = await getFacultySubmissionReview(submissionId);
      setDetail(d && typeof d === 'object' ? d : null);
    } catch (e) {
      setDetail(null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const submitOverride = async (e) => {
    e.preventDefault();
    if (!submissionId) return;
    setOverrideLoading(true);
    setOverrideMsg('');
    try {
      const body = {};
      if (overrideForm.totalScore !== '' && overrideForm.totalScore != null) {
        const n = Number(overrideForm.totalScore);
        if (!Number.isNaN(n)) body.totalScore = n;
      }
      if (overrideForm.grade.trim()) body.grade = overrideForm.grade.trim();
      if (overrideForm.remarks.trim()) body.remarks = overrideForm.remarks.trim();

      if (Object.keys(body).length === 0) {
        setOverrideMsg('Enter total score, grade, and/or remarks.');
        setOverrideLoading(false);
        return;
      }

      const updated = await postFacultySubmissionOverride(submissionId, body);
      setDetail(updated && typeof updated === 'object' ? updated : detail);
      setOverrideMsg('Override saved.');
    } catch (err) {
      setOverrideMsg(err?.response?.data?.message || err?.message || 'Override failed');
    } finally {
      setOverrideLoading(false);
    }
  };

  const flags = detail?.flags || {};
  const crit = useMemo(() => (Array.isArray(flags.critical) ? flags.critical : []), [flags.critical]);
  const warn = useMemo(() => (Array.isArray(flags.warning) ? flags.warning : []), [flags.warning]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    const backTo = facultyResultsListPath(null, searchParams);
    return (
      <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
        <Alert severity="error">{error || 'Not found'}</Alert>
        <Button component={RouterLink} to={backTo} sx={{ mt: 2 }}>
          Back to table
        </Button>
      </Box>
    );
  }

  const resultsListPath = facultyResultsListPath(detail, searchParams);
  const breakdown = detail.breakdown || null;

  const git = detail.git || {};
  const api = detail.api || {};
  const ui = detail.ui || {};

  const breakdownGit = breakdown?.git || {};
  const breakdownApi = breakdown?.api || {};
  const breakdownUi = breakdown?.ui || {};
  const studentHeaderText = (() => {
    const name =
      typeof detail?.studentName === 'string' && detail.studentName.trim()
        ? detail.studentName.trim()
        : typeof detail?.student?.name === 'string' && detail.student.name.trim()
          ? detail.student.name.trim()
          : '';
    const roll =
      detail?.studentRollNumber != null && String(detail.studentRollNumber).trim()
        ? String(detail.studentRollNumber).trim()
        : detail?.student?.rollNumber != null && String(detail.student.rollNumber).trim()
          ? String(detail.student.rollNumber).trim()
          : '';
    if (name && roll) return `${name} (${roll})`;
    if (name) return name;
    if (roll) return roll;
    return String(detail?.studentId || '—');
  })();

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      <Button component={RouterLink} to={resultsListPath} size="small" sx={{ mb: 2 }}>
        ← Results table
      </Button>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        Submission review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {detail.lab?.title ? `${detail.lab.title} · ` : ''}
        Student <span>{studentHeaderText}</span> · Status{' '}
        <strong>{detail.status}</strong>
      </Typography>

      <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Override panel
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Optional totalScore (0–100), grade (A–F), and remarks. At least one field required by API; remarks fill audit
          reason when provided.
        </Typography>
        {overrideMsg ? (
          <Alert
            severity={overrideMsg.includes('saved') ? 'success' : 'error'}
            sx={{ mb: 1.5 }}
            onClose={() => setOverrideMsg('')}
          >
            {overrideMsg}
          </Alert>
        ) : null}
        <Box component="form" onSubmit={submitOverride} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            type="number"
            label="Total score"
            value={overrideForm.totalScore}
            onChange={(e) => setOverrideForm((f) => ({ ...f, totalScore: e.target.value }))}
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            select
            label="Grade"
            value={overrideForm.grade}
            onChange={(e) => setOverrideForm((f) => ({ ...f, grade: e.target.value }))}
            sx={{ width: 120 }}
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="B">B</MenuItem>
            <MenuItem value="C">C</MenuItem>
            <MenuItem value="F">F</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="Remarks / reason"
            value={overrideForm.remarks}
            onChange={(e) => setOverrideForm((f) => ({ ...f, remarks: e.target.value }))}
            multiline
            minRows={2}
            sx={{ flex: '1 1 240px', minWidth: 200 }}
          />
          <Button type="submit" variant="contained" disabled={overrideLoading} sx={{ mt: 0.5 }}>
            {overrideLoading ? 'Saving…' : 'Apply override'}
          </Button>
        </Box>
        {detail.override ? (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Current override
            </Typography>
            <JsonPreview value={detail.override} maxHeight={160} />
          </Box>
        ) : null}
      </Paper>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell width={180}>Display score / grade</TableCell>
              <TableCell>
                <strong>{formatScore(detail.totalScore)}</strong> / <strong>{detail.grade ?? '—'}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Automated</TableCell>
              <TableCell>
                {detail.automatedEvaluation
                  ? `${formatScore(detail.automatedEvaluation.totalScore)} / ${detail.automatedEvaluation.grade ?? '—'}`
                  : '—'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Verdict</TableCell>
              <TableCell>{detail.verdict ?? '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Repo / API base</TableCell>
              <TableCell>
                {detail.repoUrl ? (
                  <Link
                    href={detail.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    underline="hover"
                    noWrap
                    title={detail.repoUrl}
                    sx={{ display: 'inline-block', maxWidth: '100%' }}
                  >
                    {detail.repoUrl}
                  </Link>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
                <Typography variant="body2" noWrap title={detail.apiBaseUrl || ''}>
                  {detail.apiBaseUrl || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <ExpandBlock title="Score breakdown" defaultOpen>
        <ScoreBreakdownTable breakdown={breakdown} />
      </ExpandBlock>

      <ExpandBlock
        title="Git evaluation"
        rightSlot={<ScoreBadge score={git.score ?? breakdownGit.score} maxScore={breakdownGit.maxScore} />}
      >
        <GitEvaluationTable git={git} />
        <EvaluatorFlagsBlock flags={git.flags} />
      </ExpandBlock>

      <ExpandBlock
        title="API test cases"
        rightSlot={<ScoreBadge score={api.score ?? breakdownApi.score} maxScore={breakdownApi.maxScore} />}
      >
        <ApiEvaluationTable api={api} />
        <EvaluatorFlagsBlock flags={api.flags} />
      </ExpandBlock>

      <ExpandBlock
        title="UI evaluation"
        rightSlot={<ScoreBadge score={ui.score ?? breakdownUi.score} maxScore={breakdownUi.maxScore} />}
      >
        <UiEvaluationTable ui={ui} />
        <EvaluatorFlagsBlock flags={ui.flags} />
      </ExpandBlock>

      <ExpandBlock
        title="Flags & remarks"
        subtitle={`${crit.length} critical · ${warn.length} warning · ${Array.isArray(detail.remarks) ? detail.remarks.length : 0} remarks`}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Critical
            </Typography>
            <FlagsRow flags={crit} variant="critical" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Warning
            </Typography>
            <FlagsRow flags={warn} variant="warning" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Remarks
            </Typography>
            <RemarksList remarks={detail.remarks} />
          </Box>
        </Stack>
      </ExpandBlock>
    </Box>
  );
}
